/* OrderHardback (renamed-in-spirit to "Order at Lulu") — the user lands here
 * from the public nav or marketing pages. We list every cookbook they own,
 * each with two buttons:
 *   • "Free A4 PDF (ring binder)" — works for everyone, no flag needed.
 *   • "Order real book at Lulu" — flag-gated. Downloads a Lulu-compliant
 *     6×9" + bleed PDF and opens lulu.com so the user uploads + finalises.
 *
 * The previous Lulu mock cart and per-cookbook quote table were removed
 * 2026-02-23 when we switched the print partner to Lulu (Option B = full
 * redirect; Lulu handles paper, cover, shipping and payment itself).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpenText, FilePdf, ArrowSquareOut, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { cookbookPath } from "@/lib/slug";

// Per-cookbook Lulu price card — fetches live quote (softcover + hardcover) from
// the Lulu Print API the moment a cookbook row enters the viewport.
function LuluPriceCard({ cookbookId }) {
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/lulu/quote/${cookbookId}?country=GB`)
      .then((r) => { if (alive) setQuote(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Live quote unavailable"); });
    return () => { alive = false; };
  }, [cookbookId]);
  if (err) {
    return <span className="text-xs text-rf-ink-soft italic" data-testid={`lulu-quote-err-${cookbookId}`}>Live quote unavailable</span>;
  }
  if (!quote) {
    return <span className="text-xs text-rf-ink-soft italic" data-testid={`lulu-quote-loading-${cookbookId}`}>Fetching live Lulu price…</span>;
  }
  const fmt = (n) => `£${Number(n).toFixed(2)}`;
  return (
    <div className="text-xs text-rf-ink-soft" data-testid={`lulu-quote-${cookbookId}`}>
      <span className="font-bold text-rf-orange">Live Lulu quote</span> ({quote.estimated_pages} pp): paperback <b className="text-rf-ink">{fmt(quote.softcover?.total || 0)}</b>, hardcover <b className="text-rf-ink">{fmt(quote.hardcover?.total || 0)}</b> incl. UK shipping
    </div>
  );
}

export default function OrderHardback() {
  const { user, loading } = useAuth();
  const { flags, loading: flagsLoading } = useSiteFlags();
  const nav = useNavigate();
  const [cookbooks, setCookbooks] = useState([]);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/order-hardback"); return; }
    (async () => {
      try {
        const { data } = await api.get("/cookbooks");
        // Exclude system Inbox — you can't bind your inbox into a book.
        setCookbooks((data.owned || []).filter((c) => !(c.is_system && c.system_kind === "inbox")));
      } catch {
        toast.error("Could not load your cookbooks.");
      }
    })();
  }, [user, loading, nav]);

  const downloadPdf = async (cbId, mode) => {
    setBusy((s) => ({ ...s, [`${cbId}_${mode}`]: true }));
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/print/cookbook/${cbId}/${mode}`;
      const token = localStorage.getItem("rf_token");
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = mode === "lulu" ? "cookbook_Lulu_6x9_book.pdf" : "cookbook_A4_ring_binder.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      if (mode === "lulu") {
        const { data } = await api.get("/print/lulu-url");
        window.open(data.url, "_blank", "noopener,noreferrer");
        toast.success("Lulu PDF downloaded — upload it on the Lulu tab to finish ordering.", { duration: 6000 });
      } else {
        toast.success("Free A4 PDF downloaded. Print it and drop into your ring binder.");
      }
    } catch (e) {
      toast.error(e?.message || "PDF generation failed");
    } finally {
      setBusy((s) => ({ ...s, [`${cbId}_${mode}`]: false }));
    }
  };

  if (loading || flagsLoading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" /></div>;

  const luluLive = !!flags.print_orders_enabled;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-rf-orange font-bold hover:underline mb-6"><ArrowLeft size={16} /> Dashboard</Link>
      <p className="overline mb-4">Print your cookbook</p>
      <h1 className="font-display text-4xl md:text-6xl font-black text-rf-ink leading-[0.95] mb-6">Two ways<br />to print.</h1>
      <p className="text-rf-ink-soft text-lg leading-relaxed mb-12 max-w-2xl">
        Bring your digital cookbook into the kitchen. Choose <b>one-recipe-per-page A4 PDFs</b> (free, perfect for a ring binder) or a <b>real bound book printed and shipped by Lulu</b>, one of the world&apos;s largest print-on-demand companies.
      </p>

      {/* Side-by-side explainer */}
      <div className="grid md:grid-cols-2 gap-5 mb-14">
        <div className="card-recipe">
          <FilePdf size={36} weight="duotone" className="text-rf-orange mb-3" />
          <h2 className="font-display text-2xl font-black text-rf-ink mb-2">Free A4 PDF</h2>
          <ul className="text-sm text-rf-ink-soft space-y-1.5 leading-relaxed">
            <li>• One recipe per A4 page</li>
            <li>• Compact 2-column layout (ingredients + method)</li>
            <li>• Drops straight into a ring binder</li>
            <li>• Available to everyone, instant download</li>
          </ul>
        </div>
        <div className={`card-recipe ${luluLive ? "" : "opacity-70"}`}>
          <BookOpenText size={36} weight="duotone" className="text-rf-orange mb-3" />
          <h2 className="font-display text-2xl font-black text-rf-ink mb-2">Bound book via Lulu {luluLive ? "" : <span className="ml-2 chip !bg-rf-cream-alt text-xs">Coming soon</span>}</h2>
          <ul className="text-sm text-rf-ink-soft space-y-1.5 leading-relaxed">
            <li>• 6×9&quot; trim, paperback or hardcover</li>
            <li>• PDF conforms to Lulu&apos;s print specs (bleed, gutter, embedded fonts)</li>
            <li>• You choose paper, cover and shipping on lulu.com</li>
            <li>• Lulu handles payment and worldwide delivery</li>
          </ul>
        </div>
      </div>

      <h2 className="font-display text-2xl font-black text-rf-ink mb-5">Your cookbooks</h2>
      {cookbooks.length === 0 && (
        <div className="card-recipe text-center py-10 text-rf-ink-soft">
          You haven&apos;t made a cookbook yet. <Link to="/dashboard" className="text-rf-orange font-bold hover:underline">Start one</Link>.
        </div>
      )}
      <div className="space-y-3">
        {cookbooks.map((cb) => (
          <div key={cb.cookbook_id} data-testid={`order-cookbook-${cb.cookbook_id}`} className="card-recipe flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <Link to={cookbookPath(cb)} className="font-display font-black text-rf-ink text-lg hover:text-rf-orange">{cb.title || "Untitled"}</Link>
              {cb.subtitle && <p className="text-xs text-rf-ink-soft">{cb.subtitle}</p>}
              {luluLive && <div className="mt-1.5"><LuluPriceCard cookbookId={cb.cookbook_id} /></div>}
            </div>
            <button
              data-testid={`order-a4-${cb.cookbook_id}`}
              onClick={() => downloadPdf(cb.cookbook_id, "a4")}
              disabled={!!busy[`${cb.cookbook_id}_a4`]}
              className="btn-outline-ink !py-2.5 !px-4 text-sm"
            >
              <FilePdf size={16} /> {busy[`${cb.cookbook_id}_a4`] ? "Building…" : "Free A4 PDF"}
            </button>
            {luluLive && (
              <button
                data-testid={`order-lulu-${cb.cookbook_id}`}
                onClick={() => downloadPdf(cb.cookbook_id, "lulu")}
                disabled={!!busy[`${cb.cookbook_id}_lulu`]}
                className="btn-amber !py-2.5 !px-4 text-sm"
              >
                <ArrowSquareOut size={16} /> {busy[`${cb.cookbook_id}_lulu`] ? "Preparing…" : "Order at Lulu"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
